import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, tap, of } from 'rxjs';
import { ModelResource } from '../resource/model.resource';
import { isInIframe } from '../../shared/helpers/maporium.validators';
import {
  ModelDto,
  CreateModelDto,
  UpdateModelDto,
  InstantiateModelDto,
  ApplyModelDto,
  ModelsResponse
} from '@maporium-workspace/shared';
import { MapService } from './map.service';

@Injectable({ providedIn: 'root' })
export class ModelService {
  public modelsChangedSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  public currentModelsSubject: BehaviorSubject<ModelsResponse> = new BehaviorSubject<ModelsResponse>({
    nodeModels: [],
    linkModels: []
  });

  // Signal to trigger canvas refresh after model application
  private elementsRefreshSignal = signal<{ updatedElements: any[] } | null>(null);

  public getElementsRefreshSignal() {
    return this.elementsRefreshSignal.asReadonly();
  }

  public triggerElementsRefresh(updatedElements: any[]) {
    this.elementsRefreshSignal.set({ updatedElements });
  }

  constructor(
    private resource: ModelResource,
    private mapService: MapService
  ) {}

  public getAll(mapId: string) {
    if (isInIframe()) {
      return of({ nodeModels: [], linkModels: [] });
    }
    return this.resource.getAll(mapId).pipe(
      tap((models) => this.currentModelsSubject.next(models))
    );
  }

  public getAllForUser(userId: string) {
    return this.resource.getAllForUser(userId);
  }

  public getAllGlobal() {
    return this.resource.getAllGlobal();
  }

  public getOne(id: string) {
    return this.resource.getOne(id);
  }

  public save(model: CreateModelDto) {
    return this.resource.create(model).pipe(
      tap(() => {
        this.modelsChangedSubject.next();
        // Refresh current models list
        this.getAll(model.mapId).subscribe();
      })
    );
  }

  public update(id: string, model: UpdateModelDto) {
    return this.resource.update(id, model).pipe(
      tap(() => {
        this.modelsChangedSubject.next();
        // MAP-1569: refresh the models cache so views keyed off currentModelsSubject
        // (e.g. the contextual "create" submenu) pick up the change - a plain edit
        // doesn't change the map's model id set, only save()/delete() did that before.
        const currentMap = this.mapService.getCurrentSelectedMapFromStore();
        if (currentMap?.id) {
          this.getAll(currentMap.id).subscribe();
        }
      })
    );
  }

  public delete(modelId: string) {
    return this.resource.delete(modelId).pipe(
      tap(() => {
        this.modelsChangedSubject.next();
        // Refresh current models list
        const currentMap = this.mapService.getCurrentSelectedMapFromStore();
        if (currentMap?.id) {
          this.getAll(currentMap.id).subscribe();
        }
      })
    );
  }

  public instantiate(modelId: string, instantiateDto: InstantiateModelDto) {
    return this.resource.instantiate(modelId, instantiateDto);
  }

  public apply(modelId: string, applyDto: ApplyModelDto) {
    return this.resource.apply(modelId, applyDto);
  }

  public getCurrentModels(): ModelsResponse {
    return this.currentModelsSubject.value;
  }

  public getNodeModels(): ModelDto[] {
    return this.currentModelsSubject.value.nodeModels;
  }

  public getLinkModels(): ModelDto[] {
    return this.currentModelsSubject.value.linkModels;
  }

  public clearCurrentModels() {
    this.currentModelsSubject.next({ nodeModels: [], linkModels: [] });
  }

}